/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useDraggable } from "../../utils/useDraggable";
import { setColor, setFont } from "../../assets/styles/styles";

import SpotifyLogo from "../../assets/images/spotify/spotifyLogo2.png";

const SmallIframeModal = ({
    setIsSmallModalOpen,
    isSmallModalOpen,
    smallIframeUrl,
    setSmallIframeUrl,
    setOpenSpotify,
}) => {
    //     `https://widget.anghami.com/artist/72?theme=fulldark&layout=list&lang=en`
    //     // "https://open.spotify.com/embed/album/2Ui10TWQIabivg41HaHP41?utm_source=generator"

    const [parsedUrl, setParsedUrl] = useState("");
    const [minimized, setMinimized] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    // Only show Spotify connect if the iframe URL contains 'spotify'
    const isSpotify = typeof parsedUrl === "string" && parsedUrl.toLowerCase().includes("spotify");
    const isMusic =
        typeof parsedUrl === "string" &&
        (parsedUrl.toLowerCase().includes("spotify") || parsedUrl.toLowerCase().includes("anghami"));
    const [fullscreen, setFullscreen] = useState(false);

    // Make modal draggable
    const { dragProps } = useDraggable({
        initialPos: { x: window.innerWidth - 400 - 20, y: 120 },
        enabled: isSmallModalOpen,
    });

    useEffect(() => {
        if (!smallIframeUrl) return;
        // console.log("Small Iframe URL changed:", smallIframeUrl);
        const urlParts = smallIframeUrl?.split(",");
        // console.log("Parsed URL parts:", urlParts);
        if (urlParts[1]) {
            setParsedUrl(urlParts[1]);
        } else {
            setParsedUrl(smallIframeUrl);
        }
        if (urlParts[2]) {
            setFullscreen(urlParts[2].toLowerCase().includes("fullscreen"));
        }
    }, []);

    const handleClose = useCallback(() => {
        setIsSmallModalOpen(false);
        setSmallIframeUrl("");
    }, []);

    const handleMinimize = useCallback(() => {
        if (isMusic) {
            setMinimized((prev) => !prev);
            setShowOverlay(true);
        } else {
            setMinimized((prev) => !prev);
        }
    }, []);

    const handleBannerClick = useCallback(() => {
        if (!minimized) {
            setMinimized(true);
            setShowOverlay(true);
            window?.Vagon?.focus?.();
        }
    }, [minimized]);

    const handleOverlayClick = useCallback(() => {
        if (minimized) {
            setMinimized(false);
            setShowOverlay(false);
        }
    }, [minimized]);

    const handleSpotifyConnect = useCallback(() => {
        if (setOpenSpotify) setOpenSpotify(true);
        setIsSmallModalOpen(false);
    }, [setOpenSpotify, setIsSmallModalOpen]);

    return (
        <ModalContent {...dragProps} minimized={minimized} fullscreen={fullscreen}>
            <TopBanner
                minimized={minimized}
                onClick={handleBannerClick}
                style={{ cursor: !minimized ? "pointer" : "default" }}
            >
                {!minimized && isMusic && (
                    <BannerText onClick={handleMinimize}>Click here to listen while playing</BannerText>
                )}
                {minimized && isSpotify && (
                    <SpotifyConnect onClick={handleSpotifyConnect}>
                        <BannerText style={{ fontSize: "12px" }}>Connect to Spotify for full songs</BannerText>
                        <SpotifyLogoImg src={SpotifyLogo} alt="Spotify" />
                    </SpotifyConnect>
                )}
                <BannerButton onClick={handleClose} minimized={minimized} aria-label="Close">
                    ×
                </BannerButton>
                {!fullscreen && (
                    <BannerButton onClick={handleMinimize} minimized={minimized} aria-label="Minimize">
                        {minimized ? "▾" : "-"}
                    </BannerButton>
                )}
            </TopBanner>
            <IframeWrapper minimized={minimized} fullscreen={fullscreen}>
                <StyledIframe src={parsedUrl} title="Small Iframe" />
                {minimized && showOverlay && isMusic && (
                    <MusicOverlay onClick={handleOverlayClick}>
                        <OverlayText>click to control music</OverlayText>
                    </MusicOverlay>
                )}
            </IframeWrapper>
            {isMusic && <WarningText>Music may pause if you click outside</WarningText>}
        </ModalContent>
    );
};

//  have them "minimize to listen while playing", which forces the refocus - then when it's minimized it shows an overlay to "click to control music"
//  only show the "connect to spotify for full music" if the URL contains spotify

SmallIframeModal.propTypes = {
    setIsSmallModalOpen: PropTypes.func.isRequired,
    setSmallIframeUrl: PropTypes.func.isRequired,
    smallIframeUrl: PropTypes.bool,
    isSmallModalOpen: PropTypes.bool,
};

export default SmallIframeModal;

const BANNER_HEIGHT = 38;
const WARNING_HEIGHT = 28; // px, matches WarningText padding and font size

const ModalContent = styled.div`
    position: fixed;
    top: ${({ fullscreen, minimized }) => (fullscreen ? "2vh" : minimized ? "15%" : "23%")};
    left: ${({ fullscreen }) => (fullscreen ? "0" : "auto")};
    right: ${({ fullscreen, minimized }) => (fullscreen ? "0" : minimized ? "8%" : "10%")};
    background: ${setColor.darkPurpleBackground};
    border-radius: ${({ fullscreen }) => (fullscreen ? "0" : "15px")};
    box-shadow: 0 4px 5px rgba(0, 0, 0, 0.3);
    width: ${(props) => {
        if (props.fullscreen) return "100vw";
        if (props.minimized) return "330px";
        return "400px";
    }};
    height: ${(props) => {
        if (props.fullscreen) return "100vh";
        if (props.minimized) return `${BANNER_HEIGHT + 114 + WARNING_HEIGHT + 30}px`;
        return `${BANNER_HEIGHT + 362 + WARNING_HEIGHT}px`;
    }};
    max-width: ${({ fullscreen }) => (fullscreen ? "100vw" : "80vw")};
    max-height: ${({ fullscreen }) => (fullscreen ? "98vh" : "70vh")};
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    overflow: hidden;
    z-index: 99999;

    @media (max-width: 768px) and (orientation: landscape) {
        height: ${({ fullscreen }) => (fullscreen ? "100vh" : `${BANNER_HEIGHT + 114}px`)};
        width: ${({ fullscreen }) => (fullscreen ? "100vw" : "45%")};
    }

    @media (max-height: 501px) and (max-width: 900px) and (orientation: landscape) {
        max-height: ${({ fullscreen }) => (fullscreen ? "100vh" : `${BANNER_HEIGHT + 114}px`)};
        top: ${({ fullscreen }) => (fullscreen ? "0" : "15vh")};
        width: ${({ fullscreen }) => (fullscreen ? "100vw" : "40%")};
    }

    @media (max-width: 768px) and (orientation: portrait) {
        position: fixed;
        top: ${({ fullscreen }) => (fullscreen ? "0" : "22vh")};
        left: ${({ fullscreen }) => (fullscreen ? "0" : "90px")};
        height: ${({ fullscreen }) => (fullscreen ? "100vh" : `${BANNER_HEIGHT + 114}px`)};
        width: ${({ fullscreen }) => (fullscreen ? "100vw" : "40%")};
    }

    @media (orientation: portrait) and (min-width: 900px) and (min-height: 501px) {
        position: fixed;
        bottom: ${({ fullscreen }) => (fullscreen ? "0" : "0vh")};
        left: ${({ fullscreen }) => (fullscreen ? "0" : "30px")};
        transform: none;
        z-index: 100000;
        height: ${({ fullscreen }) => (fullscreen ? "100vh" : `${BANNER_HEIGHT + 114}px`)};
    }
`;

const TopBanner = styled.div`
    width: 100%;
    height: ${BANNER_HEIGHT}px;
    background: ${setColor.darkPurpleBackground};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 10px;
    box-sizing: border-box;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    z-index: 200;
`;

const BannerButton = styled.button`
    background-color: transparent;
    border: none;
    color: white;
    font-size: ${({ minimized }) => (minimized ? "1.3rem" : "1.7rem")};
    cursor: pointer;
    margin-left: 10px;
    margin-right: 0;
    height: 28px;
    width: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: normal;
    line-height: 1;
    padding: 0;
    transition:
        font-size 0.2s,
        height 0.2s,
        width 0.2s;
    @media (max-width: 768px) {
        font-size: 1.2rem;
        height: 22px;
        width: 22px;
    }
`;

const IframeWrapper = styled.div`
    width: ${({ fullscreen }) => (fullscreen ? "100vw" : "100%")};
    height: ${({ fullscreen, minimized }) =>
        fullscreen ? "100vh" : minimized ? `calc(100% - ${BANNER_HEIGHT}px + 5px)` : `calc(100% - ${BANNER_HEIGHT}px)`};
    position: relative;
    background: ${setColor.darkPurpleBackground};
    /* border-bottom-left-radius: ${({ fullscreen }) => (fullscreen ? "0" : "15px")};
    border-bottom-right-radius: ${({ fullscreen }) => (fullscreen ? "0" : "15px")}; */
    overflow: hidden;
`;

const StyledIframe = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
    background: ${setColor.darkPurpleBackground};
`;

const BannerText = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    margin-right: auto;
    margin-left: 10px;
    letter-spacing: 0.02em;
    user-select: none;
    font-family: ${setFont.sansPro};

    @media (max-width: 768px) or (max-height: 501px) {
        font-size: 12px;
    }
`;

const MusicOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    z-index: 120000;
    cursor: pointer;
`;
const SpotifyConnect = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
    z-index: 120000;
    cursor: pointer;
`;

const OverlayText = styled.span`
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.9);
    padding: 40px 48px;
    border-radius: 8px;
    user-select: none;
    margin-top: 27px;
`;
const WarningText = styled.p`
    color: #fff;
    font-size: 12px;
    font-weight: 300;
    padding: 5px 10px;
    align-self: center;
    font-family: ${setFont.sansPro};
`;

const SpotifyLogoImg = styled.img`
    height: 22px;
    width: 22px;
    margin-left: 10px;
    margin-right: 10px;
    cursor: pointer;
    transition: filter 0.2s;
    filter: brightness(1.2);
    &:hover {
        filter: brightness(2);
    }
`;
